import { FloorplanRevision } from '@thive/fddm-types';

import { capitalizeFirstLetter } from '@/helpers/string';
import { DRAWABLE_TOOL_IDS } from '@/modules/toolbar/constants';
import { DrawableToolId } from '@/modules/toolbar/types';
import {
  areasToFlatCollection,
  templatesToFlatCollection,
} from '@/modules/floorplanDesign/helpers/helpers';

// Will return "1" from "Intake Group 1"
// eslint-disable-next-line no-useless-escape
const INCREMENT = /(\ \d+)$/;

export const initializeNames = (revision: FloorplanRevision): void => {
  const { areas, obstacles, logicalSpaces, templates, flows } = revision.design;
  const areaNames = areasToFlatCollection(areas).map((area) => area.name);
  const obstacleNames = obstacles.map((obstacle) => obstacle.name);
  const logicalSpaceNames = logicalSpaces.map((logicalSpace) => logicalSpace.name);
  const templateNames = templatesToFlatCollection(templates).map((template) => template.group.name);
  const flowNames = flows.flows.map((flow) => flow.name);

  // Tool names
  DRAWABLE_TOOL_IDS.forEach((tool) => {
    const toolNameWithSpace = tool.split('_').join(' ');
    const names = [...areaNames, ...obstacleNames, ...logicalSpaceNames].filter((name) =>
      name.toLowerCase().startsWith(toolNameWithSpace),
    );

    if (names.length === 0) {
      sessionStorage.setItem(tool, '1');
      return;
    }

    const maxIndex = Math.max(
      ...names.map((name) => {
        const increment = INCREMENT.exec(name);
        return increment === null ? 1 : parseInt(increment[0].trim()) + 1;
      }),
    );

    sessionStorage.setItem(tool, maxIndex.toString());
  });

  // Areas, Obstacles and Logical spaces
  [...areaNames, ...obstacleNames, ...logicalSpaceNames].forEach((name) => {
    const increment = INCREMENT.exec(name);
    const baseName = increment === null ? name : name.replace(INCREMENT, '').trim();
    const storageKey = baseName.toLowerCase().replace(/\s+/g, '_');
    const storageValue = sessionStorage.getItem(storageKey);

    const storageIndex = storageValue === null ? 1 : parseInt(storageValue);
    const index =
      increment === null ? storageIndex : Math.max(storageIndex, parseInt(increment[0].trim()) + 1);
    sessionStorage.setItem(storageKey, index.toString());
  });

  // Templates
  ['shuttle', 'lassie']
    .map((tool) => tool.substring(0, 2))
    .forEach((tool) => {
      const names = templateNames.filter((name) => name.toLowerCase().startsWith(tool));

      if (names.length === 0) {
        sessionStorage.setItem(tool, '1');
        return;
      }

      const maxIndex = Math.max(
        ...names.map((name) => {
          const increment = name.substring(2, 4);
          return increment === null ? 1 : parseInt(increment) + 1;
        }),
      );

      sessionStorage.setItem(tool, maxIndex.toString());
    });

  // Flows
  const names = flowNames.filter((name) => name.toLowerCase().startsWith('flow'));

  if (names.length === 0) {
    sessionStorage.setItem('flow', '1');
    return;
  }

  const maxIndex = Math.max(
    ...names.map((name) => {
      const increment = INCREMENT.exec(name);
      return increment === null ? 1 : parseInt(increment[0].trim()) + 1;
    }),
  );

  sessionStorage.setItem('flow', maxIndex.toString());
};

export const getName = (tool: DrawableToolId): string => {
  const index = parseInt(sessionStorage.getItem(tool) ?? '1');
  sessionStorage.setItem(tool, (index + 1).toString());
  return `${tool
    .split('_')
    .map((item) => capitalizeFirstLetter(item))
    .join(' ')} ${index}`;
};

/**
 * Get an incremented name (e.g. Intake 2) from a name (e.g. Intake 1)
 *
 * @param name The name to increment.
 */

export const incrementName = (name: string): string => {
  const increment = INCREMENT.exec(name);
  const baseName = increment === null ? name : name.replace(INCREMENT, '').trim();
  const storageKey = baseName.toLowerCase().replace(/\s+/g, '_');
  const storageValue = sessionStorage.getItem(storageKey);

  if (storageValue === null) {
    const index = increment === null ? 1 : parseInt(increment[0].trim()) + 1;
    sessionStorage.setItem(storageKey, (index + 1).toString());
    return `${baseName} ${index}`;
  }

  const index = parseInt(storageValue);
  sessionStorage.setItem(storageKey, (index + 1).toString());
  return `${baseName} ${index}`;
};

/**
 * Get an incremented name (e.g. Intake group 2) from a name (e.g. Intake group)
 *
 * @param name The name to increment.
 */

export const generateNewGroupName = (name: string): string => {
  const increment = INCREMENT.exec(name);
  const groupName = increment === null ? name : name.replace(INCREMENT, '').trim();
  const storageKey = `${groupName.toLowerCase()}`;
  const index = parseInt(sessionStorage.getItem(storageKey) ?? '1');
  sessionStorage.setItem(storageKey, (index + 1).toString());

  return `${groupName
    .split('_')
    .map((item) => capitalizeFirstLetter(item))
    .join(' ')} ${index}`;
};

export const generateTemplateGroupName = (
  templateType: 'lassie' | 'lassie_mini' | 'shuttle' | 'hik',
): string => {
  const storageKey = templateType.substring(0, 2);
  const index = parseInt(sessionStorage.getItem(storageKey) ?? '1');
  sessionStorage.setItem(storageKey, (index + 1).toString());
  return `${storageKey.toUpperCase()}${String(index).padStart(2, '0')}`;
};
